import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { trackingClient } from "@/lib/tracking/client";
import { Shield, X } from "lucide-react";
import { TrackingErrorBoundary } from "./TrackingErrorBoundary";

export function ConsentBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    // Check if consent was already given
    const stored = localStorage.getItem('tracking_consent');
    if (!stored) {
      // Show banner after short delay
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    trackingClient.setConsent(true, true);
    setShow(false);
  };

  const handleRejectAll = () => {
    trackingClient.setConsent(false, false);
    setShow(false);
  };

  const handleSavePreferences = () => {
    trackingClient.setConsent(analytics, ads);
    setShow(false);
  };

  if (!show) return null;

  return (
    <TrackingErrorBoundary>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom-5">
        <Card className="max-w-4xl mx-auto p-6 shadow-lg">
        <div className="flex items-start gap-4">
          <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">Your Privacy Matters</h3>
            <p className="text-sm text-muted-foreground mb-4">
              We use cookies and similar technologies to improve your experience, analyze our traffic, 
              and show you personalized content. You can choose which types of cookies to accept.
            </p>

            {showDetails && (
              <div className="space-y-4 mb-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="analytics-consent">Analytics Cookies</Label>
                    <p className="text-xs text-muted-foreground">
                      Help us understand how visitors interact with our website
                    </p>
                  </div>
                  <Switch
                    id="analytics-consent"
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="ads-consent">Advertising Cookies</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable personalized advertising and measure campaign performance
                    </p>
                  </div>
                  <Switch
                    id="ads-consent"
                    checked={ads}
                    onCheckedChange={setAds}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {!showDetails ? (
                <>
                  <Button onClick={handleAcceptAll} size="sm">
                    Accept All
                  </Button>
                  <Button onClick={handleRejectAll} variant="outline" size="sm">
                    Reject All
                  </Button>
                  <Button 
                    onClick={() => setShowDetails(true)} 
                    variant="ghost" 
                    size="sm"
                  >
                    Customize
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={handleSavePreferences} size="sm">
                    Save Preferences
                  </Button>
                  <Button 
                    onClick={() => setShowDetails(false)} 
                    variant="ghost" 
                    size="sm"
                  >
                    Back
                  </Button>
                </>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={() => setShow(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
    </TrackingErrorBoundary>
  );
}
